import type { AnchorHTMLAttributes, ComponentPropsWithoutRef, ReactNode } from 'react';

import { Icon, type IconName } from '../Icon';
import styles from './Link.module.css';

export type LinkType = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'type'> {
  children: ReactNode;
  disabled?: boolean;
  leadingIcon?: IconName;
  /** 常态弱化：保留所属语义色的悬停效果，只把常态压到次级文字色，用于「未授权」「暂无」这类空态。 */
  muted?: boolean;
  /**
   * 视觉禁用但保留响应能力：带上禁用样式与 `aria-disabled`，但**不加原生 disabled**，
   * 因此仍能收到点击。用于「点了以后告诉用户为什么不能用」——原生 disabled 会吞掉点击事件。
   */
  softDisabled?: boolean;
  trailingIcon?: IconName;
  type?: LinkType;
  /** 需要保留常态色、但在悬停时表达另一语义时使用，例如删除操作的危险色反馈。 */
  hoverType?: LinkType;
  /** @deprecated 保留旧调用兼容；文字按钮统一颜色反馈，不再显示悬停下划线。 */
  underline?: boolean;
}

export function Link({ children, className, disabled, href, hoverType, leadingIcon, muted, softDisabled, trailingIcon, type = 'primary', underline = false, ...anchorProps }: LinkProps) {
  const classes = [styles.root, className].filter(Boolean).join(' ');
  const dataState = {
    'aria-disabled': softDisabled || undefined,
    'data-disabled': disabled || softDisabled || undefined,
    'data-has-icon': Boolean(leadingIcon || trailingIcon) || undefined,
    'data-hover-type': hoverType,
    'data-muted': muted || undefined,
    'data-type': type,
    'data-underline': underline || undefined,
  };
  const content = <>
    {leadingIcon ? <Icon name={leadingIcon} size="sm" /> : null}
    <span>{children}</span>
    {trailingIcon ? <Icon name={trailingIcon} size="sm" /> : null}
  </>;

  if (href && !disabled) {
    return <a {...anchorProps} {...dataState} className={classes} href={href}>{content}</a>;
  }

  /* 无 href 时退回原生 button，透传事件与 aria/data 属性；
     两种宿主元素的事件类型不同，这里按 button 侧收窄（Link 的公开契约以 onClick 为主）。 */
  const buttonProps = anchorProps;

  return <button {...(buttonProps as ComponentPropsWithoutRef<'button'>)} className={classes} {...dataState} disabled={disabled} type="button">{content}</button>;
}
